import { AppMainSection, useAppSelector, useHomeLogic, useLogoutLogic, UserState } from 'azshared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu } from '../../components/menu/menu';
import { useGlobalActions } from '../../hooks/global-actions';
import { menuItems } from '../../utils/menu-items';
import { AppMenuHeader } from './components/menu-header';
import './home-container.scss';

export const HomeContainer = () => {
  const globalActions = useGlobalActions();
  const { selectedSection, onSelectedSectionChanged, onProfileIconPress } = useHomeLogic();
  const { isLoggingOut, onLogout } = useLogoutLogic(globalActions);
  const { name, surname, informativeMode, businessName, userType, fiduciaria } = useAppSelector<UserState>((state) => state.data.user);
  const menuItemsByRole = useMemo(
    () =>
      menuItems
        .filter((item) => !informativeMode || (item.section !== 'documents' && item.section !== 'sign'))
        .filter((item) => (fiduciaria ? item.section !== 'sign' : true)),
    [fiduciaria, informativeMode]
  );

  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const [closeGestureOffset, setCloseGestureOffset] = useState<number>(0);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const shouldCloseMenuOnEnd = useRef<boolean>(false);
  const SWIPE_THRESHOLD = 15;

  const toggleMenu = () => {
    setMenuOpened((prev) => !prev);
  };

  const isPortraitOrientation = () => {
    return window.innerHeight > window.innerWidth;
  };

  const handleTouchStart = (e: TouchEvent) => {
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    shouldCloseMenuOnEnd.current = false;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchStartPos.current || !menuOpened) return;

    e.preventDefault();

    const touchCurrentPos = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };

    const deltaX = touchStartPos.current.x - touchCurrentPos.x;
    const deltaY = touchStartPos.current.y - touchCurrentPos.y;

    const isPortrait = isPortraitOrientation();

    // Portrait: close menu on swipe down (deltaY < -SWIPE_THRESHOLD)
    if (isPortrait && deltaY < -SWIPE_THRESHOLD) {
      shouldCloseMenuOnEnd.current = true;
      // Apply transform - move menu down as user swipes down
      setCloseGestureOffset(-deltaY);
    }
    // Landscape: close menu on swipe left (deltaX > SWIPE_THRESHOLD)
    else if (!isPortrait && deltaX > SWIPE_THRESHOLD) {
      shouldCloseMenuOnEnd.current = true;
      // Apply transform - move menu left as user swipes left
      setCloseGestureOffset(deltaX);
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (shouldCloseMenuOnEnd.current) {
      e.preventDefault();
      setCloseGestureOffset(0);
      setMenuOpened(false);
    }
    touchStartPos.current = null;
    shouldCloseMenuOnEnd.current = false;
  };

  useEffect(() => {
    if (!menuOpened) return;

    document.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
    document.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    document.addEventListener('touchend', handleTouchEnd as EventListener, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart as EventListener);
      document.removeEventListener('touchmove', handleTouchMove as EventListener);
      document.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [menuOpened]);

  const handleSelectedSection = useCallback(
    (section?: AppMainSection) => {
      if (onSelectedSectionChanged) {
        onSelectedSectionChanged(section);
        setMenuOpened(false);
      }
    },
    [onSelectedSectionChanged]
  );

  const handleProfileIconPress = useCallback(
    (section?: AppMainSection) => {
      if (onProfileIconPress) {
        onProfileIconPress();
        setMenuOpened(false);
      }
    },
    [onProfileIconPress]
  );

  return (
    <div className='home-container'>
      <Menu
        menuItems={menuItemsByRole}
        selectedSection={selectedSection || 'home'}
        onSelectedSectionChanged={handleSelectedSection}
        onLogout={onLogout}
        isLoggingOut={isLoggingOut}
        opened={menuOpened}
        onToggle={toggleMenu}
        closeGestureOffset={closeGestureOffset}>
        <AppMenuHeader onProfileIconPress={handleProfileIconPress} name={name} surname={surname} businessName={businessName} userType={userType} />
      </Menu>

      <div className='content'>
        <Outlet />
      </div>
    </div>
  );
};
